import React, { useState, useEffect, useRef } from 'react';
import { useInstallPrompt } from '../components/InstallPrompt';
import Icon from '../components/ui/Icon';
import './LandingPage.css';

const LOGO = process.env.PUBLIC_URL + '/icone+logo.png';
const ICONE = process.env.PUBLIC_URL + '/icone-sem-fundo.png';

const P = process.env.PUBLIC_URL + '/prints/';
const prints = [
  { src: P + 'printHome.jpeg', caption: 'Painel inicial da fazenda' },
  { src: P + 'printCustos.jpeg', caption: 'Lançamento de custos' },
  { src: P + 'printCustosResumo.jpeg', caption: 'Resumo de custos por safra' },
  { src: P + 'printResultado1.jpeg', caption: 'Receita, custo e lucro da safra' },
  { src: P + 'printResultado2.jpeg', caption: 'Resultado por área de cultivo' },
  { src: P + 'printVneda.jpeg', caption: 'Vendas e comercialização' },
  { src: P + 'printEstoqueInsumo.jpeg', caption: 'Estoque de insumos e produção' },
  { src: P + 'printCustosHistorico.jpeg', caption: 'Histórico de custos e produtividade' },
];

const funcionalidades = [
  { icon: 'safra', titulo: 'Safras e ciclos', desc: 'Cada ciclo separado, do plantio à colheita — grão, hortaliça ou fruta.' },
  { icon: 'manejo', titulo: 'Custos e despesas', desc: 'Insumo, diária, arrendamento. Você vê quanto custa produzir de verdade.' },
  { icon: 'colheita', titulo: 'Produção', desc: 'Registre em kg, saco, caixa ou bandeja, por armazém e por área.' },
  { icon: 'venda', titulo: 'Vendas', desc: 'Quanto saiu, a quanto e a receita por safra — sem planilha.' },
  { icon: 'estoque', titulo: 'Estoque', desc: 'Produção menos venda, atualizado sozinho. Saldo por unidade.' },
  { icon: 'resultado', titulo: 'Resultado', desc: 'Lucro, custo por hectare e comparação entre áreas e safras.' },
  { icon: 'dinheiro', titulo: 'Financeiro', desc: 'Livro caixa, contas a pagar e a receber. O dinheiro da fazenda no mesmo lugar.' },
  { icon: 'insumo', titulo: 'Insumos', desc: 'Compras e saldo de semente, adubo e defensivo, com o preço pago.' },
  { icon: 'maquinario', titulo: 'Maquinário', desc: 'Manutenções do trator e das máquinas, com histórico fácil de acompanhar.' },
  { icon: 'usuarios', titulo: 'Equipe', desc: 'Família ou funcionário entra com código. Você aprova quem vê os dados.' },
];

const perguntas = [
  { q: 'Preciso pagar para testar?', a: 'Não. São 60 dias com tudo liberado, sem cartão e sem compromisso.' },
  { q: 'Como funciona o pagamento depois?', a: 'Se quiser continuar, o Pix é a partir de R$ 6,99 por mês. Você paga mês a mês, sem fidelidade.' },
  { q: 'E se eu não quiser mais usar?', a: 'É só parar de pagar. Sem multa e sem cancelamento burocrático. Os dados ficam guardados se você voltar.' },
  { q: 'Serve para o meu cultivo?', a: 'Sim. Grãos, hortaliça em estufa, fruta e outros. Unidades e relatórios acompanham o que você produz.' },
  { q: 'Funciona no celular?', a: 'Sim. Abre no navegador do celular ou do computador, e dá para instalar como aplicativo — sem loja. No celular, use “Instalar agora” ou o menu do navegador.' },
  { q: 'Ajuda no Imposto de Renda?', a: 'Sim. Receita e despesa ficam organizadas o ano inteiro, com base para o Livro Caixa do Produtor Rural (LCDPR) e menos trabalho para o contador.' },
  { q: 'Posso compartilhar a fazenda?', a: 'Sim. Você recebe um código, convida quem quiser e aprova cada acesso. Cada pessoa entra com a própria senha.' },
  { q: 'Meus dados estão seguros?', a: 'Sim. Cada fazenda fica isolada. Só entra quem você autorizar.' },
];

function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.lp-reveal');
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); });
    }, { threshold: 0.12 });
    els.forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

function Faq({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="lp-faq-item">
      <button type="button" className="lp-faq-q" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        {item.q}
        <span className={`lp-faq-icon ${open ? 'open' : ''}`} aria-hidden="true">
          <Icon nome="chevron-baixo" tamanho={18} />
        </span>
      </button>
      {open && <div className="lp-faq-a">{item.a}</div>}
    </div>
  );
}

function ShotsCarrossel() {
  const [idx, setIdx] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const intervalRef = useRef(null);

  const reset = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => setIdx(i => (i + 1) % prints.length), 5000);
  };
  useEffect(() => { reset(); return () => clearInterval(intervalRef.current); }, []);

  const ir = (dir) => { setIdx(i => (i + dir + prints.length) % prints.length); reset(); };
  const tStart = (e) => setTouchStart(e.touches[0].clientX);
  const tEnd = (e) => {
    if (touchStart === null) return;
    const diff = touchStart - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) ir(diff > 0 ? 1 : -1);
    setTouchStart(null);
  };

  return (
    <div className="lp-shots">
      <div className="lp-shots-viewport" onTouchStart={tStart} onTouchEnd={tEnd}>
        <button type="button" className="lp-shots-arrow left" onClick={() => ir(-1)} aria-label="Anterior">
          <Icon nome="chevron-direita" tamanho={20} />
        </button>
        <div className="lp-shots-track" style={{ transform: `translateX(-${idx * 100}%)` }}>
          {prints.map((p, i) => (
            <div className="lp-shot" key={p.src}>
              <img src={p.src} alt={p.caption} loading="lazy" />
            </div>
          ))}
        </div>
        <button type="button" className="lp-shots-arrow right" onClick={() => ir(1)} aria-label="Próximo">
          <Icon nome="chevron-direita" tamanho={20} />
        </button>
      </div>
      <div className="lp-shots-caption">{prints[idx].caption}</div>
      <div className="lp-shots-dots">
        {prints.map((p, i) => (
          <button
            type="button"
            key={p.src}
            className={`lp-shots-dot ${i === idx ? 'active' : ''}`}
            onClick={() => { setIdx(i); reset(); }}
            aria-label={p.caption}
            aria-current={i === idx ? 'true' : undefined}
          />
        ))}
      </div>
    </div>
  );
}

export default function LandingPage({ onIrLogin }) {
  const [scrolled, setScrolled] = useState(false);
  const { canInstall, install } = useInstallPrompt();
  useReveal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="lp">
      <header className={`lp-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="lp-header-inner">
          <button type="button" className="lp-brand" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} aria-label="AgriGest — início">
            <span className="lp-logo-chip">
              <img src={ICONE} alt="" className="lp-logo-img" />
            </span>
            <span className="lp-brand-name">AgriGest</span>
          </button>
          <nav className="lp-nav" aria-label="Seções">
            <button type="button" className="lp-nav-link" onClick={() => scrollToId('funcionalidades')}>O sistema</button>
            <button type="button" className="lp-nav-link" onClick={() => scrollToId('video')}>Como usar</button>
            <button type="button" className="lp-nav-link" onClick={() => scrollToId('preco')}>Preço</button>
          </nav>
          <div className="lp-header-actions">
            <button type="button" className="lp-btn-entrar" onClick={() => onIrLogin()}>Entrar</button>
            <button type="button" className="lp-btn-cadastrar" onClick={() => onIrLogin('criar')}>Cadastrar</button>
          </div>
        </div>
      </header>

      <div className="lp-bg">
        <section className="lp-hero">
          <div className="lp-hero-marca">
            <img src={LOGO} alt="AgriGest — Gestão Agrícola" className="lp-hero-logo" />
            <div className="lp-badge"><Icon nome="presente" tamanho={14} /> 60 dias grátis</div>
          </div>
          <h1 className="lp-h1 lp-serif">
            Saiba o que a safra <span className="hl">realmente deixou</span>
          </h1>
          <p className="lp-sub">
            Custo, produção, venda e estoque no mesmo lugar — no celular, no campo.
            Para grão, hortaliça, fruta e o que você cultiva.
          </p>
          <button type="button" className="lp-btn-cta" onClick={() => onIrLogin('criar')}>
            Começar grátis por 60 dias
          </button>
          <p className="lp-hero-note">Sem cartão · Sem fidelidade · Pix quando quiser continuar</p>
        </section>

        <div className="lp-stats">
          <div className="lp-stat">
            <div className="lp-stat-num lp-serif">60 dias</div>
            <div className="lp-stat-label">para testar tudo</div>
          </div>
          <div className="lp-stat">
            <div className="lp-stat-num lp-serif">R$ 6,99</div>
            <div className="lp-stat-label">a partir de / mês</div>
          </div>
        </div>
      </div>

      <div className="lp-light">
        <section className="lp-section" id="funcionalidades">
          <h2 className="lp-section-title lp-serif lp-reveal">Do plantio à venda, organizado</h2>
          <p className="lp-section-desc lp-reveal">O que você registra no dia a dia vira resultado, estoque, financeiro e base para o IR.</p>
          <div className="lp-feat-grid">
            {funcionalidades.map((f, i) => (
              <div className="lp-feat-card lp-reveal" key={f.titulo} style={{ transitionDelay: `${i * 50}ms` }}>
                <div className="lp-feat-icon"><Icon nome={f.icon} tamanho={22} /></div>
                <h3>{f.titulo}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="lp-bg">
        <section className="lp-section">
          <h2 className="lp-section-title lp-serif lp-reveal">Olha como fica por dentro</h2>
          <p className="lp-section-desc lp-reveal">O AgriGest como o produtor usa no dia a dia.</p>
          <div className="lp-reveal"><ShotsCarrossel /></div>
        </section>
      </div>

      <div className="lp-light">
        <section className="lp-section" id="video">
          <h2 className="lp-section-title lp-serif lp-reveal">Veja na prática</h2>
          <p className="lp-section-desc lp-reveal">Um tutorial curto de como lançar e acompanhar a safra.</p>
          <div className="lp-video-wrap lp-reveal">
            <video controls playsInline preload="metadata" className="lp-video">
              <source src={process.env.PUBLIC_URL + '/tutorial-agrigest.mp4'} type="video/mp4" />
              Seu navegador não suporta vídeo HTML5.
            </video>
          </div>
        </section>
      </div>

      <div className="lp-bg">
        <section className="lp-section">
          <div className="lp-fiscal lp-reveal">
            <span className="lp-fiscal-badge"><Icon nome="documento" tamanho={14} /> Imposto de renda</span>
            <h2 className="lp-serif">Chegue na declaração com a casa arrumada</h2>
            <p>
              Receita e despesa ficam registradas o ano inteiro. No IR você não recomeça do zero —
              e o contador ganha a base do Livro Caixa do Produtor Rural (LCDPR).
            </p>
            <ul className="lp-fiscal-list">
              <li><Icon nome="ok" tamanho={16} /> Vendas organizadas por safra e período</li>
              <li><Icon nome="ok" tamanho={16} /> Custos por categoria, prontos para conferir</li>
              <li><Icon nome="ok" tamanho={16} /> Base para o LCDPR, sem planilha paralela</li>
            </ul>
          </div>
        </section>

        <section className="lp-section" id="preco">
          <h2 className="lp-section-title lp-serif lp-reveal">Simples e justo</h2>
          <p className="lp-section-desc lp-reveal">Paga só se continuar usando. Sem surpresa no cartão.</p>
          <div className="lp-price-wrap lp-reveal">
            <div className="lp-price-card">
              <div className="lp-price-head">
                <span className="lp-price-prefix">depois dos 60 dias grátis, a partir de</span>
                <span className="lp-price-tag">R$ 6,99</span>
                <span className="lp-price-periodo"> /mês</span>
              </div>
              <div className="lp-price-item">
                <span className="lp-price-check"><Icon nome="ok" tamanho={14} /></span>
                <div><b>60 dias grátis</b><br /><span>Tudo liberado, sem cartão e sem limite artificial</span></div>
              </div>
              <div className="lp-price-item">
                <span className="lp-price-check"><Icon nome="ok" tamanho={14} /></span>
                <div><b>Pix, mês a mês</b><br /><span>Sem taxa de cartão e sem débito automático</span></div>
              </div>
              <div className="lp-price-item">
                <span className="lp-price-check"><Icon nome="ok" tamanho={14} /></span>
                <div><b>Para quando quiser</b><br /><span>Parou de pagar, parou de usar. Sem multa.</span></div>
              </div>
              <button type="button" className="lp-btn-cta lp-btn-cta--bloco" onClick={() => onIrLogin('criar')}>
                Criar conta grátis
              </button>
            </div>
          </div>
        </section>
      </div>

      <div className="lp-light">
        <section className="lp-section lp-section--estreita">
          <h2 className="lp-section-title lp-serif lp-reveal">No campo ou em casa</h2>
          <p className="lp-section-desc lp-reveal">Site no navegador. App no celular, sem loja.</p>
          <div className="lp-disp-grid lp-reveal">
            <div className="lp-disp-card">
              <span className="lp-disp-icon"><Icon nome="web" tamanho={32} /></span>
              <div className="lp-disp-nome">Navegador</div>
              <div className="lp-disp-desc">Computador ou celular, sem instalar</div>
              <span className="lp-disp-badge">Disponível</span>
            </div>
            <div className="lp-disp-card">
              <span className="lp-disp-icon"><Icon nome="celular" tamanho={32} /></span>
              <div className="lp-disp-nome">Android e iPhone</div>
              <div className="lp-disp-desc">Instala pelo próprio site</div>
              <span className="lp-disp-badge">Disponível</span>
              {canInstall ? (
                <button type="button" className="lp-btn-install" onClick={install}>Instalar agora</button>
              ) : (
                <div className="lp-disp-install-tip">
                  No celular: menu do navegador → Instalar aplicativo
                </div>
              )}
            </div>
          </div>
        </section>
      </div>

      <div className="lp-bg">
        <section className="lp-section">
          <h2 className="lp-section-title lp-serif lp-reveal">Família e equipe na mesma fazenda</h2>
          <p className="lp-section-desc lp-reveal">Cada um com a própria senha. Você aprova quem entra.</p>
          <div className="lp-share lp-reveal">
            <div className="lp-share-steps">
              <div className="lp-share-step">
                <div className="lp-share-num">1</div>
                <div>
                  <h4>Você recebe um código</h4>
                  <p>Ao cadastrar a fazenda, o código aparece no menu — no formato <span className="lp-share-code">AGR-XXXXXX</span>.</p>
                </div>
              </div>
              <div className="lp-share-step">
                <div className="lp-share-num">2</div>
                <div>
                  <h4>Envie para quem confia</h4>
                  <p>WhatsApp, mensagem ou pessoalmente. Só quem tem o código consegue pedir acesso.</p>
                </div>
              </div>
              <div className="lp-share-step">
                <div className="lp-share-num">3</div>
                <div>
                  <h4>A pessoa se cadastra</h4>
                  <p>Em “Entrar em fazenda existente” ela informa o código e cria usuário e senha.</p>
                </div>
              </div>
              <div className="lp-share-step">
                <div className="lp-share-num">4</div>
                <div>
                  <h4>Você aprova</h4>
                  <p>O pedido chega em Notificações. Até aprovar, ela não vê dado nenhum da fazenda.</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div className="lp-light">
        <section className="lp-section">
          <h2 className="lp-section-title lp-serif lp-reveal">Perguntas frequentes</h2>
          <p className="lp-section-desc lp-reveal">O que o produtor mais pergunta antes de criar a conta.</p>
          <div className="lp-faq lp-reveal">
            {perguntas.map(p => <Faq key={p.q} item={p} />)}
          </div>
        </section>
      </div>

      <section className="lp-cta">
        <h2 className="lp-serif lp-reveal">Organize a safra deste ciclo</h2>
        <p className="lp-reveal">Cadastro rápido. 60 dias para usar tudo, sem cartão.</p>
        <button type="button" className="lp-btn-cta lp-reveal" onClick={() => onIrLogin('criar')}>
          Criar minha conta grátis
        </button>
      </section>

      <footer className="lp-footer">
        <a href="https://wa.me/5554999487383?text=Ol%C3%A1!%20Gostaria%20de%20saber%20mais%20sobre%20o%20AgriGest"
          target="_blank" rel="noopener noreferrer" className="lp-footer-link">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
          Fale conosco pelo WhatsApp
        </a>
        <p className="lp-copy">© 2026 AgriGest — Gestão Agrícola. Todos os direitos reservados.</p>
      </footer>
    </div>
  );
}
